import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { JobCategoryWithTags, JobTag } from "@/types/db";
import type { LocationsResponse } from "@/lib/locations";
import { cn } from "@/lib/utils";

export type SelectedFilter = {
  categoryId: number;
  categoryName: string;
  tag: JobTag;
};

interface SearchFilterProps {
  categoriesWithTags: JobCategoryWithTags[];
  locations: LocationsResponse;
  selectedFilters?: SelectedFilter[];
  onSelectFilter?: (filter: SelectedFilter) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  activeCountry?: string;
  activeState?: string;
}

const pillClass = cn(
  "inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-semibold",
  "transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:outline-none",
  "sm:h-8",
);

export default function SearchFilter({
  categoriesWithTags,
  locations,
  selectedFilters = [],
  onSelectFilter,
  searchQuery = "",
  onSearchChange,
  activeCountry,
  activeState,
}: SearchFilterProps) {
  const [regionOpen, setRegionOpen] = useState(
    !!activeCountry || !!activeState,
  );
  const selectedCategoryIds = new Set(selectedFilters.map((f) => f.categoryId));

  const activeCountryData = activeCountry
    ? locations.countries.find(
        (c) => c.value.toLowerCase() === activeCountry.toLowerCase(),
      )
    : null;
  const states = activeCountryData?.states ?? [];

  return (
    <div className="w-full">
      <div className="rounded-xl bg-white px-6 py-5 shadow-sm ring-1 ring-black/5">
        <ul className="flex list-none flex-col gap-6" role="list">
          <li className="flex w-full flex-col gap-3">
            <p className="text-polaris-navy text-sm font-semibold">Filter</p>
            <div className="flex items-center rounded-lg ring-1 ring-gray-300 transition-shadow focus-within:ring-2 focus-within:ring-orange-300">
              <svg
                className="ml-3 size-4 shrink-0 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="search"
                name="q"
                aria-label="Search jobs"
                autoCorrect="off"
                placeholder="Search jobs..."
                className="flex-1 bg-transparent p-2 text-base outline-none sm:text-sm"
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
              />
            </div>
          </li>

          {categoriesWithTags.map((category) => (
            <li
              key={category.id}
              className="filterGroup refinement flex w-full flex-col gap-3"
            >
              <p className="text-polaris-navy text-sm font-semibold">
                {category.name}
              </p>
              <ul
                className="flex w-full flex-wrap gap-2"
                id={`filter-category-${category.id}`}
                role="list"
              >
                {category.jobTags.map((tag) => {
                  const isSelected = selectedFilters.some(
                    (f) => f.categoryId === category.id && f.tag.id === tag.id,
                  );
                  const categoryHasSelection = selectedCategoryIds.has(
                    category.id,
                  );
                  const isDisabled = categoryHasSelection && !isSelected;

                  return (
                    <li key={tag.id}>
                      <button
                        type="button"
                        disabled={isDisabled}
                        aria-pressed={isSelected}
                        onClick={() =>
                          onSelectFilter?.({
                            categoryId: category.id,
                            categoryName: category.name,
                            tag,
                          })
                        }
                        className={cn(
                          pillClass,
                          isSelected
                            ? "border-orange-400 bg-orange-400 text-white"
                            : isDisabled
                              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                              : "cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50",
                        )}
                      >
                        {tag.name.charAt(0).toUpperCase() + tag.name.slice(1)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}

          <li className="filterGroup refinement flex w-full flex-col gap-3">
            <button
              className="flex items-center gap-x-1 rounded focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:outline-none"
              type="button"
              aria-expanded={regionOpen}
              onClick={() => setRegionOpen((o) => !o)}
            >
              <svg
                className={cn(
                  "size-4 transition-transform duration-200",
                  regionOpen ? "rotate-90" : "",
                )}
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M7 5l6 5-6 5" />
              </svg>
              <p className="text-polaris-navy text-sm font-semibold">Region</p>
            </button>

            {regionOpen && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <p className="text-polaris-navy text-sm font-semibold">
                    Country
                  </p>
                  <ul className="flex w-full flex-wrap gap-2" role="list">
                    {locations.countries.map((c) => {
                      const isActive =
                        activeCountry?.toLowerCase() === c.value.toLowerCase();
                      return (
                        <li key={c.value}>
                          <Link
                            to="/search"
                            search={isActive ? {} : { country: c.value }}
                            className={cn(
                              pillClass,
                              isActive
                                ? "border-orange-400 bg-orange-400 text-white"
                                : "border-gray-300 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50",
                            )}
                          >
                            {c.name}{" "}
                            <span
                              className={cn(
                                "ml-1 tabular-nums",
                                isActive ? "text-white/70" : "text-gray-500",
                              )}
                            >
                              ({c.jobCount})
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {activeCountry && states.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <p className="text-polaris-navy text-sm font-semibold">
                      State/Province
                    </p>
                    <ul className="flex w-full flex-wrap gap-2" role="list">
                      {states.map((s) => {
                        const isActive =
                          activeState?.toLowerCase() === s.value.toLowerCase();
                        return (
                          <li key={s.value}>
                            <Link
                              to="/search"
                              search={
                                isActive
                                  ? { country: activeCountry }
                                  : { country: activeCountry, state: s.value }
                              }
                              className={cn(
                                pillClass,
                                isActive
                                  ? "border-orange-400 bg-orange-400 text-white"
                                  : "border-gray-300 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50",
                              )}
                            >
                              {s.name}{" "}
                              <span
                                className={cn(
                                  "ml-1 tabular-nums",
                                  isActive ? "text-white/70" : "text-gray-500",
                                )}
                              >
                                ({s.jobCount})
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}
